import React from "react";
import { Button } from "@/components/ui/button";

type Props = { fallback?: React.ReactNode };

type State = { hasError: boolean; error?: Error };

export class ErrorBoundary extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('ErrorBoundary', error, info);
    const w = window as any;
    if (Array.isArray(w.__BOOT_ERRORS)) {
      w.__BOOT_ERRORS.push({ type: 'react_error', message: error?.message, stack: info?.componentStack });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <main className="container mx-auto py-10">
          <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>
          {this.state.error?.message && (
            <p className="text-destructive font-mono text-sm mb-4">{this.state.error.message}</p>
          )}
          <p className="text-muted-foreground mb-4">Please refresh the page. If the problem persists, contact support.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.handleRetry}>Try Again</Button>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </main>
      );
    }
    return this.props.children as React.ReactNode;
  }
}

